import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

import { HeroExtraBlock } from 'apps/promo/components/acceptInvite/HeroExtraBlock';
import ColoredSectionContainer from 'apps/promo/components/common/ColoredSectionContainer';
import { PromoPageContainer } from 'apps/promo/components/common/promo-page-container/PromoPageContainer';
import FaqSection from 'apps/promo/components/common/sections/FaqSection';
import HeadingSection from 'apps/promo/components/common/sections/HeadingSection';
import { HeroSection } from 'apps/promo/components/common/sections/HeroSection';
import { TilesSection } from 'apps/promo/components/common/sections/TilesSection';
import TrustpilotSection from 'apps/promo/components/common/sections/TrustpilotSection';
import NotAuthBasePage from 'apps/promo/components/NotAuthBasePage';
import PromoPopup from 'apps/promo/components/popup/PromoPopup';
import { useGetQueryParams } from 'apps/promo/components/utils';
import { DEFAULT_COUNTRY } from 'apps/promo/constants/Constants';
import { NO_AUTH_LOGIN_REGISTRATION_FORM_NAME } from 'apps/promo/constants/formName';
import { loadSmartScript } from 'apps/promo/helpers/oneLinkSmartScript';
import { getLangCountryPrefix, resolveArguments } from 'apps/promo/helpers/utils';
import { useForm } from 'apps/promo/hooks/selectors';

import { referralClickHandler } from './ReferralUtils';

const AcceptInvitePage = (props) => {
    const [inviteCode, setInviteCode] = useState('');
    const popupRef = useRef(null);
    const form = useForm(NO_AUTH_LOGIN_REGISTRATION_FORM_NAME);
    const urlParams = useGetQueryParams();

    const noteText = props.heroNote;
    const prefix = getLangCountryPrefix(props.lang, props.country);

    const addPrefixToLinks = (text, prefix) => {
        return text?.replace(/<a\s+href="\/(.*?)">(.*?)<\/a>/g, (_, path, linkText) => `<a href="${prefix}/${path}">${linkText}</a>`);
    };

    const updatedNoteText = addPrefixToLinks(noteText, prefix);

    useEffect(() => {
        let pathname = location.pathname;
        let arr = pathname.split('/');
        let code = arr.pop();
        setInviteCode(code);
        void loadSmartScript();
    }, []);

    const isBtnDisabled = !(form?.fields?.phone_number?.value && (form?.fields?.phone_prefix?.value + form?.fields?.phone_number?.value).length >= 6);

    const getHeroTitle = () => {
        let heroTitle = props.heroTitle;
        if (urlParams?.n) {
            heroTitle = resolveArguments(props.heroTitlePersonal, urlParams?.n);
        } else if (props.inviterName) {
            heroTitle = resolveArguments(props.heroTitlePersonal, props.inviterName);
        }

        return heroTitle;
    };

    const resolveCurrentCountryCode = () => {
        if (props.availableCountries && props.country) {
            const existingCountry = props.availableCountries.find((c) => c.code_a2.toLowerCase() === props.country.toLowerCase());
            if (existingCountry) {
                return existingCountry.code_a2.toLowerCase();
            }
        }
        return DEFAULT_COUNTRY;
    };

    return (
        <>
            <PromoPageContainer bgColor="eggshell03" noIndent>
                {props.heroTitle && (
                    <HeroSection
                        title={getHeroTitle()}
                        text={props.heroText}
                        bgImg={props.heroBgImg}
                        img={props.heroImg}
                        data={props.heroData}
                        note={updatedNoteText}
                        horizontalLayout
                        hasQrCode
                        extraComponent={
                            <HeroExtraBlock
                                currentCountry={resolveCurrentCountryCode()}
                                countries={props.availableCountries}
                                labelText={props.inputLabel}
                                btnTitle={props.heroData?.extra_button?.title}
                                isMobileDevice={props.isMobileDevice}
                                disabledBtn={isBtnDisabled}
                                onExtraButtonClick={() =>
                                    referralClickHandler(form, popupRef, inviteCode, props.lang, props.isMobileDevice, 'accept-invite-page', false)
                                }
                                searchPlaceholder={props.searchPlaceholder}
                                searchFullPlaceholder={props.searchFullPlaceholder}
                                searchModalTitle={props.searchModalTitle}
                                searchNoResult={props.searchNoResult}
                            />
                        }
                    />
                )}

                <ColoredSectionContainer>
                    {props.headline1Title && <HeadingSection title={props.headline1Title} text={props.headline1Text} />}

                    {props.lang && <TrustpilotSection lang={props.lang} />}

                    {props.headline2Title && <HeadingSection title={props.headline2Title} text={props.headline2Text} />}
                    {props.tiles && <TilesSection data={props.tiles} lang={props.lang} country={props.country} loadingLazy grayText />}
                </ColoredSectionContainer>

                {props.faqTitle && props.questions && <FaqSection title={props.faqTitle} questions={props.questions} noTopPadding />}
            </PromoPageContainer>
            <PromoPopup ref={popupRef} />
        </>
    );
};

const mapStateToProps = (state) => {
    return {
        inviterName: state.extra?.strapiApi?.inviterName,

        heroTitle: state.extra?.strapiApi?.hero_block?.title,
        heroTitlePersonal: state.extra?.strapiApi?.hero_block_title_personal,
        heroText: state.extra?.strapiApi?.hero_block?.text,
        heroData: state.extra?.strapiApi?.hero_block,
        heroBgImg: state.extra?.strapiApi?.hero_block?.bgImg,
        heroImg: state.extra?.strapiApi?.hero_block?.img,
        heroNote: state.extra?.strapiApi?.hero_block?.note,

        inputLabel: state.extra?.strapiApi?.input_label,

        headline1Title: state.extra?.strapiApi?.headline_1?.title,
        headline1Text: state.extra?.strapiApi?.headline_1?.text,

        headline2Title: state.extra?.strapiApi?.headline_2?.title,
        headline2Text: state.extra?.strapiApi?.headline_2?.text,

        tiles: state.extra?.strapiApi?.tiles_section,

        faqTitle: state.extra?.strapiApi?.faq?.title,
        questions: state.extra?.strapiApi?.faq?.questions,

        isMobileDevice: state.extra?.isMobile,

        availableCountries: state.extra?.strapiApi?.availableCountries,

        searchPlaceholder: state.extra?.strapiApi?.search_placeholder,
        searchFullPlaceholder: state.extra?.strapiApi?.search_full_placeholder,
        searchModalTitle: state.extra?.strapiApi?.search_modal_title,
        searchNoResult: state.extra?.strapiApi?.search_no_result,

        lang: state.extra.noAuthFooter?.language?.lang,
        country: state.extra.noAuthFooter?.language?.country,
    };
};

export default connect(mapStateToProps)(NotAuthBasePage(AcceptInvitePage));
